// Statement classification helper (SqlGrammar2 PRD:320, deep review §4.6
// prerequisite). Built ON TOP of `coarseKind` — it delegates the statement KIND
// to the existing first-token classifier and layers on a deterministic,
// kind-level RISK via the Decision-3 table. No tree walk, no clause analysis:
// UPDATE/DELETE-without-WHERE escalation and routine-body inspection stay out
// (the dangerous-query gate keeps its own heuristics until #13 slice 4).

import type { SqlEngine, StatementKind, StatementRisk } from '../model';
import { coarseKind } from './coarseKind';

export interface StatementClassification {
  readonly kind: StatementKind;
  readonly risk: StatementRisk;
}

// Decision 3 — deterministic kind→risk mapping. Declared as an exhaustive
// `Record<StatementKind, StatementRisk>` on purpose: adding a new StatementKind
// to the model union without deciding its risk here becomes a compile error.
//
// Notes on the two non-obvious buckets:
//  - `delete` maps to `destructive` (NOT `write-data`) to match the app's
//    destructive-confirm category (DROP / DELETE / TRUNCATE).
//  - `call` / `procedural-body` map to `unknown`: their true effect depends on
//    the routine body, which this coarse layer deliberately does not inspect.
const KIND_TO_RISK: Record<StatementKind, StatementRisk> = {
  select: 'read',
  explain: 'read',
  insert: 'write-data',
  update: 'write-data',
  merge: 'write-data',
  delete: 'destructive',
  'ddl-drop': 'destructive',
  'ddl-truncate': 'destructive',
  'ddl-create': 'write-schema',
  'ddl-alter': 'write-schema',
  'ddl-rename': 'write-schema',
  grant: 'write-schema',
  revoke: 'write-schema',
  'transaction-control': 'session',
  'session-setting': 'session',
  pragma: 'session',
  'client-directive': 'session',
  'batch-separator': 'session',
  call: 'unknown',
  'procedural-body': 'unknown',
  unknown: 'unknown',
};

/**
 * The Decision-3 kind→risk table, exported for reuse (e.g. structure.ts /
 * createForkNormalizer populating `StatementInfo.risk`, and future consumers).
 */
export function riskForKind(kind: StatementKind): StatementRisk {
  return KIND_TO_RISK[kind];
}

/**
 * Classify a single statement's text into `{ kind, risk }`.
 *
 * `kind` comes from {@link coarseKind} (first-token match, CTE-aware, never
 * falling back to `'select'`); `risk` comes from {@link riskForKind}.
 *
 * @param text   The statement text (leading whitespace/comments are tolerated —
 *               `coarseKind` strips them).
 * @param engine The dialect. Threaded through the signature now for API
 *               stability: the v1 mapping is dialect-INDEPENDENT, but #13's
 *               per-dialect refinements will consume it without a signature
 *               change for any caller.
 */
export function classifyStatement(text: string, engine: SqlEngine): StatementClassification {
  // `engine` is intentionally unused in v1 — see the JSDoc above. Referencing it
  // keeps `noUnusedParameters` happy while preserving the stable signature.
  void engine;
  const kind = coarseKind(text);
  return { kind, risk: riskForKind(kind) };
}
